/**
 * A loading component useful as a placeholder while the Knowledge Base is fetching data.
 *
 * @module
 */
import * as React from 'react';
import cx from 'classnames';
import { Helmet } from './meta';
import { CSSTransition } from 'react-transition-group';
import { addClassName } from '../utils/classes';
import { useSelector } from '../actions/types';
import { createAction } from '../utils/types';
// TODO: Move logic into a hook.
const UPDATE_TIME = 40;
const PROGRESS_INCREASE = 5;
// const DELAY = 100;
const LOADER_STYLES = `
  .elevio__loading-bar {
    transition: opacity 300ms, transform 200ms linear 0s;
    width: 100%;
    will-change: transform, opacity;
    height: 2px;
    background-color: blue;
    box-shadow: rgb(0, 102, 255) 0px 1px 5px 2px;
    position: fixed;
    top: 0;
  }

  .loading-enter {
    opacity: 0;
  }
  .loading-enter-active {
    opacity: 1;
  }
  .loading-exit {
    opacity: 1;
  }
  .loading-exit-active {
    opacity: 0.5;
  }
  .loading-exit-done {
    opacity: 0;
  }
`;
function buildStyle(percent, styleProp, direction = 'ltr') {
    const coefficient = direction === 'rtl' ? 1 : -1;
    const tx = (100 - percent) * coefficient;
    const style = {
        zIndex: 999999,
        transform: `translate3d(${tx}%, 0px, 0px)`,
    };
    return { ...style, ...styleProp };
}
const Actions = {
    start: () => createAction('start'),
    tick: () => createAction('tick'),
    finish: () => createAction('finish'),
    reset: () => createAction('reset'),
};
const initialState = {
    percent: 10,
    status: 'hidden',
};
function reducer(state, action) {
    switch (action.type) {
        case 'start': {
            return { status: 'running', percent: 10 };
        }
        case 'tick': {
            if (state.status !== 'running') {
                return state;
            }
            // Use cosine as a smoothing function
            // It could be any function to slow down progress near the ending 100%
            const smoothedProgressIncrease = Math.floor(PROGRESS_INCREASE * Math.cos(state.percent * (Math.PI / 2 / 100)));
            const newPercent = state.percent === 100
                ? state.percent
                : state.percent + smoothedProgressIncrease;
            if (state.percent === newPercent)
                return state;
            return {
                ...state,
                percent: newPercent,
            };
        }
        case 'finish':
            return { status: 'running', percent: 100 };
        case 'reset':
            return { status: 'hidden', percent: 0 };
    }
    return state;
}
/**
 * A loading component useful as a placeholder while the Knowledge Base is fetching data.
 *
 * Usage example:
 * ```javascript
 * import LoadingBar from '@elevio/kb-kit/lib/components/loading';
 *
 * const customStyle: React.CSSProperties = {
 *   background: 'rgb(255, 255, 204)',
 * };
 * const customClasses: string = 'loady-mc-loadface';
 *
 * const MyCustomLoadingBar = () => {
 *   return <LoadingBar className={customClasses} style={customStyle} />;
 * };
 * ```
 */
export const LoadingBar = ({ className, style, ...props }) => {
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const loadingPage = useSelector((_state) => _state.page.loadingPage);
    const nodeRef = React.useRef(null);
    React.useEffect(() => {
        let progressIntervalId = null;
        let finishTimeoutId = null;
        const simulateProgress = () => {
            dispatch(Actions.tick());
        };
        if (!loadingPage) {
            progressIntervalId && clearInterval(progressIntervalId);
            dispatch(Actions.finish());
            new Promise((r) => setTimeout(r, 200)).then(() => dispatch(Actions.reset()));
        }
        else if (loadingPage) {
            dispatch(Actions.start());
            progressIntervalId = window.setInterval(simulateProgress, UPDATE_TIME);
        }
        return () => {
            progressIntervalId && clearInterval(progressIntervalId);
            finishTimeoutId && clearTimeout(finishTimeoutId);
        };
    }, [loadingPage]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("style", null, LOADER_STYLES)),
        React.createElement(CSSTransition, { nodeRef: nodeRef, in: state.status !== 'hidden', timeout: 300, classNames: "loading", unmountOnExit: true },
            React.createElement("div", Object.assign({ ref: nodeRef, style: buildStyle(state.percent, style), className: cx(className, state.status, addClassName('loading-bar')) }, props)))));
};
LoadingBar.displayName = 'LoadingBar';
//# sourceMappingURL=loading.js.map